import { MaterialKey } from './types';

// Per-thickness gauge data. Numbers are sourced/derived from public sheet metal
// fabricator specs (SendCutSend, OSHCut, Onshape gauge tables) — these are
// reasonable defaults for design-time calculations, not a substitute for the
// supplier's confirmed values for production.
//
// minFlangeLength heuristic: ~4*T + R (industry rule of thumb for V-die ~6-8*T).
// K-factor: lower for ductile aluminum, higher for steel/stainless (more elastic).
export interface Gauge {
  thickness: number;       // mm
  kFactor: number;
  defaultBendRadius: number; // mm
  minFlangeLength: number;   // mm
}

export interface MaterialPreset {
  key: MaterialKey;
  label: string;
  density: number;         // g/cm^3
  color: string;
  // Bend angle limits per SendCutSend: 5°–130° (so user-entered |angle| in this range)
  minBendAngle: number;    // degrees
  maxBendAngle: number;
  gauges: Gauge[];
}

// defaultBendRadius reflects the natural inside radius from the V=8T standard
// shop practice (16% rule snapped to a stocked punch). kFactor here is reference
// only — actual K is computed per bend via getKFactor(material, R, T).
// defaultBendRadius is fixed at 1.0 mm across all thicknesses and materials per
// shop preference. Note: for thick stock (e.g. T=6 with R=1) this is below the
// natural V-die radius and below R ≥ T best practice — validation will warn,
// and the user should pick an appropriate V-die for the actual press-brake.
const mildSteelGauges: Gauge[] = [
  { thickness: 0.5, kFactor: 0.38, defaultBendRadius: 1.0, minFlangeLength: 3.5 },
  { thickness: 0.8, kFactor: 0.38, defaultBendRadius: 1.0, minFlangeLength: 4.5 },
  { thickness: 1.0, kFactor: 0.38, defaultBendRadius: 1.0, minFlangeLength: 5.5 },
  { thickness: 1.2, kFactor: 0.38, defaultBendRadius: 1.0, minFlangeLength: 6.0 },
  { thickness: 1.5, kFactor: 0.38, defaultBendRadius: 1.0, minFlangeLength: 7.5 },
  { thickness: 2.0, kFactor: 0.43, defaultBendRadius: 1.0, minFlangeLength: 9.0 },
  { thickness: 2.5, kFactor: 0.43, defaultBendRadius: 1.0, minFlangeLength: 11.0 },
  { thickness: 3.0, kFactor: 0.43, defaultBendRadius: 1.0, minFlangeLength: 13.0 },
  { thickness: 4.0, kFactor: 0.43, defaultBendRadius: 1.0, minFlangeLength: 17.0 },
  { thickness: 5.0, kFactor: 0.43, defaultBendRadius: 1.0, minFlangeLength: 21.0 },
  { thickness: 6.0, kFactor: 0.50, defaultBendRadius: 1.0, minFlangeLength: 25.0 },
];

const ss304Gauges: Gauge[] = [
  { thickness: 0.5, kFactor: 0.40, defaultBendRadius: 1.0, minFlangeLength: 4.0 },
  { thickness: 0.8, kFactor: 0.40, defaultBendRadius: 1.0, minFlangeLength: 5.0 },
  { thickness: 1.0, kFactor: 0.45, defaultBendRadius: 1.0, minFlangeLength: 6.0 },
  { thickness: 1.2, kFactor: 0.45, defaultBendRadius: 1.0, minFlangeLength: 7.0 },
  { thickness: 1.5, kFactor: 0.45, defaultBendRadius: 1.0, minFlangeLength: 8.5 },
  { thickness: 2.0, kFactor: 0.45, defaultBendRadius: 1.0, minFlangeLength: 10.0 },
  { thickness: 2.5, kFactor: 0.45, defaultBendRadius: 1.0, minFlangeLength: 12.0 },
  { thickness: 3.0, kFactor: 0.45, defaultBendRadius: 1.0, minFlangeLength: 14.0 },
  { thickness: 4.0, kFactor: 0.45, defaultBendRadius: 1.0, minFlangeLength: 18.0 },
  { thickness: 5.0, kFactor: 0.45, defaultBendRadius: 1.0, minFlangeLength: 22.0 },
  { thickness: 6.0, kFactor: 0.50, defaultBendRadius: 1.0, minFlangeLength: 26.0 },
];

const al5052Gauges: Gauge[] = [
  { thickness: 1.0, kFactor: 0.40, defaultBendRadius: 1.0, minFlangeLength: 6.0 },
  { thickness: 1.2, kFactor: 0.40, defaultBendRadius: 1.0, minFlangeLength: 7.0 },
  { thickness: 1.5, kFactor: 0.40, defaultBendRadius: 1.0, minFlangeLength: 8.0 },
  { thickness: 2.0, kFactor: 0.40, defaultBendRadius: 1.0, minFlangeLength: 10.0 },
  { thickness: 2.5, kFactor: 0.40, defaultBendRadius: 1.0, minFlangeLength: 12.0 },
  { thickness: 3.0, kFactor: 0.40, defaultBendRadius: 1.0, minFlangeLength: 14.0 },
  { thickness: 4.0, kFactor: 0.40, defaultBendRadius: 1.0, minFlangeLength: 18.0 },
  { thickness: 5.0, kFactor: 0.40, defaultBendRadius: 1.0, minFlangeLength: 22.0 },
  { thickness: 6.0, kFactor: 0.50, defaultBendRadius: 1.0, minFlangeLength: 26.0 },
];

export const MATERIALS: Record<MaterialKey, MaterialPreset> = {
  ss304: {
    key: 'ss304',
    label: 'Stainless 304',
    density: 8.0,
    color: '#c8ccd1',
    minBendAngle: 5,
    maxBendAngle: 130,
    gauges: ss304Gauges,
  },
  mild: {
    key: 'mild',
    label: 'Mild Steel',
    density: 7.85,
    color: '#9aa0a8',
    minBendAngle: 5,
    maxBendAngle: 130,
    gauges: mildSteelGauges,
  },
  al5052: {
    key: 'al5052',
    label: 'Aluminum 5052-H32',
    density: 2.68,
    color: '#d6d8dc',
    minBendAngle: 5,
    maxBendAngle: 130,
    gauges: al5052Gauges,
  },
};

export const MATERIAL_LIST = Object.values(MATERIALS);

// Find the gauge entry closest to a given thickness for a material.
export function nearestGauge(material: MaterialKey, thickness: number): Gauge {
  const list = MATERIALS[material].gauges;
  let best = list[0];
  let bestDist = Math.abs(list[0].thickness - thickness);
  for (const g of list) {
    const d = Math.abs(g.thickness - thickness);
    if (d < bestDist) { best = g; bestDist = d; }
  }
  return best;
}

// Standard K-factor table for AIR BENDING (the press-brake method used by laser
// fab shops like SendCutSend, OSHCut, Laserhub). Source: sheetmetal.me reference
// chart and matching shop K-tables; cross-checked against a reference CAD output
// (R=1, T=1, 90°, outside 50+100 → flat = 148.09 mm, which requires K = 0.33).
//
// The table groups by R/T ratio:
//   R < 2T   → tight bend, neutral axis stays close to inside of the bend
//   R = 2-3T → typical, neutral axis migrating toward center
//   R > 3T   → large radius, neutral axis ≈ center → K → 0.5
//
// Mild steel and aluminum use the "soft" column; SS304 sits one tick stiffer.
const K_TABLE: Record<MaterialKey, Array<{ maxRT: number; k: number }>> = {
  al5052: [
    { maxRT: 2, k: 0.33 },
    { maxRT: 3, k: 0.40 },
    { maxRT: Infinity, k: 0.50 },
  ],
  mild: [
    { maxRT: 2, k: 0.33 },
    { maxRT: 3, k: 0.40 },
    { maxRT: Infinity, k: 0.50 },
  ],
  ss304: [
    { maxRT: 2, k: 0.38 },
    { maxRT: 3, k: 0.43 },
    { maxRT: Infinity, k: 0.50 },
  ],
};

// K-factor for a specific bend, derived from R/T ratio per industry standard.
// More accurate than a single global K because each bend in the part can have
// different R, and the standard K depends on R/T.
export function getKFactor(material: MaterialKey, bendRadius: number, thickness: number): number {
  if (thickness <= 0) return 0.45;
  const ratio = bendRadius / thickness;
  const bands = K_TABLE[material] ?? K_TABLE.mild;
  for (const b of bands) {
    if (ratio <= b.maxRT) return b.k;
  }
  return 0.5;
}

// =====================================================================
// V-die selection & natural radius (the "16% rule" for air bending)
// =====================================================================
//
// In air bending, the inside radius is determined by the V-die opening, not
// the punch tip radius (as long as the punch is sharp enough). The natural
// inside radius forms as the sheet spans the V shoulders:
//
//   R_inside ≈ V × factor
//
// where `factor` depends on material springback (more springback = larger R).
// Mild steel: ~0.156, Stainless: ~0.170, Aluminum: ~0.135.

export const NATURAL_RADIUS_FACTOR: Record<MaterialKey, number> = {
  al5052: 0.135,
  mild: 0.156,
  ss304: 0.170,
};

// V-die openings commonly stocked at job shops (mm).
export const STANDARD_V_DIES = [4, 6, 8, 10, 12, 16, 20, 24, 32, 40, 48];

// Punch tip radii commonly stocked (mm). Bend radius snaps to these values.
export const STANDARD_PUNCH_RADII = [
  0.4, 0.6, 0.8, 1.0, 1.2, 1.5, 2.0, 2.5, 3.0, 4.0, 5.0, 6.0, 8.0, 10.0,
];

function snapToList(value: number, list: number[]): number {
  let best = list[0];
  let bestDist = Math.abs(value - list[0]);
  for (const v of list) {
    const d = Math.abs(value - v);
    if (d < bestDist) { best = v; bestDist = d; }
  }
  return best;
}

// Recommended V-die for a thickness (V = 8T standard practice). Snapped to
// nearest stocked die opening.
export function recommendedVDie(thickness: number): number {
  return snapToList(thickness * 8, STANDARD_V_DIES);
}

// Inside bend radius produced by a given V-die in given material (16% rule),
// snapped to the nearest stocked punch radius.
export function radiusFromVDie(material: MaterialKey, vDie: number): number {
  const factor = NATURAL_RADIUS_FACTOR[material] ?? 0.156;
  return snapToList(vDie * factor, STANDARD_PUNCH_RADII);
}

// Reverse: given a bend radius, what V-die would naturally produce it?
// Returns the closest standard V-die.
export function vDieFromRadius(material: MaterialKey, R: number): number {
  const factor = NATURAL_RADIUS_FACTOR[material] ?? 0.156;
  return snapToList(R / factor, STANDARD_V_DIES);
}
